import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../shared/Seo';

const COMPARISON_TABLE = [
  {
    physical: 'Requires visiting a store',
    online: 'Accessible from anywhere',
  },
  {
    physical: 'Limited machine availability',
    online: 'Wider series selection',
  },
  {
    physical: 'Capsule dispensed instantly',
    online: 'Items shipped later',
  },
];

const OnlineGachaponPage = () => {
  useEffect(() => {
    const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const ensureTop = () => {
      const doc = document?.documentElement;
      const body = document?.body;
      const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
      if (current > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };

    smoothScroll();
    const retryId = window.setTimeout(ensureTop, 220);
    const fallbackId = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 900);

    return () => {
      window.clearTimeout(retryId);
      window.clearTimeout(fallbackId);
    };
  }, []);

  return (
    <div className="flex flex-col gap-8 px-4 pt-4 pb-8 sm:px-0 sm:pt-0 sm:pb-16">
      <Seo
        title="What Is Online Gachapon? | PonPon!"
        description="Learn about online gachapon: how it works, how it compares to physical machines, and why collectors worldwide use it to access Japanese capsule toys."
        canonical="https://ponpon.app/online-gachapon"
        type="article"
      />
      <header>
        <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
          What Is Online Gachapon?
        </h1>
      </header>

      <div className="mb-6 space-y-4">
        <p className="text-sm leading-relaxed text-ponpon-navy/75">
          Online gachapon lets collectors spin capsule toy machines digitally and receive real, physical
          items shipped to their door. It brings the experience of Japanese capsule toys online, without
          needing to be in Japan or a physical gachapon store.
        </p>
        <p className="text-sm leading-relaxed text-ponpon-navy/75">
          Traditional gachapon machines are everywhere in Japan, offering collectible capsule toys through
          a simple spin. Online gachapon recreates that experience digitally, allowing collectors to spin
          from anywhere in the world and receive authentic items by mail.
        </p>
      </div>

      <div className="space-y-6">
        <section className="space-y-2">
          <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">
            How Does Online Gachapon Work?
          </h2>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Online gachapon follows the same idea as physical capsule machines, just with a digital
            interface:
          </p>
          <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-ponpon-navy/75">
            <li>Choose an online gachapon machine</li>
            <li>Spin digitally, just like turning a physical capsule knob</li>
            <li>Receive a real item from the listed series</li>
            <li>Store items digitally and ship them when ready</li>
          </ul>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Each spin results in a physical collectible — not a virtual item.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">
            Online Gachapon vs. Physical Gachapon
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-ponpon-navy/20">
                  <th className="px-4 py-3 text-left font-semibold text-ponpon-navy">
                    Physical Gachapon
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-ponpon-navy">
                    Online Gachapon
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_TABLE.map((row, index) => (
                  <tr key={index} className="border-b border-ponpon-navy/10">
                    <td className="px-4 py-3 text-ponpon-navy/75">{row.physical}</td>
                    <td className="px-4 py-3 text-ponpon-navy/75">{row.online}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Online gachapon removes location limits while preserving the excitement of spinning for a
            collectible.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">
            Is Online Gachapon Legit?
          </h2>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Yes, when operated transparently, online gachapon delivers real, physical capsule toys.
          </p>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Legitimate online gachapon platforms typically offer:
          </p>
          <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-ponpon-navy/75">
            <li>Authentic merchandise</li>
            <li>Clear item lists for each machine</li>
            <li>Secure payment processing</li>
            <li>Physical shipping of items after spinning</li>
          </ul>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Collectors should always verify transparency, item authenticity, and shipping policies before
            spinning.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">
            Why Collectors Use Online Gachapon
          </h2>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Many collectors choose online gachapon because it offers:
          </p>
          <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-ponpon-navy/75">
            <li>Access to Japanese capsule toy series worldwide</li>
            <li>No need to travel to physical locations</li>
            <li>The ability to spin at any time</li>
            <li>Flexible shipping options</li>
            <li>A cleaner, more organized collecting experience</li>
          </ul>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            For international collectors, online gachapon bridges the gap between Japan-only releases and
            global access.
          </p>
        </section>
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
        >
          <span aria-hidden="true">←</span>
          Back to homepage
        </Link>
      </div>
    </div>
  );
};

export default OnlineGachaponPage;

